import React, { ReactElement } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer
} from 'recharts';
import CardSkeleton from '../../../../../components/card/cardSkeleton';
import Card from '../../../../../components/card/card';
import { useHistoryServiceListHistoryQuery } from '../../../../../api/enhanced/enhancedHistory';
import { useCheckpointServiceGetCheckpointQuery } from '../../../../../api/enhanced/enhancedCheckpoints';

interface TemperatureGraphProps {
  groupId?: string;
  buildingId?: string;
  checkpointId?: string;
  startTime?: string;
  endTime?: string;
}

function TemperatureGraph({
  checkpointId,
  groupId,
  buildingId,
  startTime,
  endTime
}: TemperatureGraphProps): ReactElement {
  const isValid =
    groupId != null &&
    groupId !== '' &&
    buildingId != null &&
    checkpointId != null;

  const { data: checkpointResponse } = useCheckpointServiceGetCheckpointQuery(
    isValid
      ? {
          checkpointId,
          groupId,
          buildingId,
          sensorType: 'SENSOR_TYPE_TEMP'
        }
      : skipToken
  );

  const {
    data: dataResponse,
    isLoading,
    isFetching
  } = useHistoryServiceListHistoryQuery(
    isValid
      ? {
          checkpointId,
          groupId,
          buildingId,
          sensorType: 'SENSOR_TYPE_TEMP',
          sortingOrder: 'asc',
          startTime,
          endTime
        }
      : skipToken
  );

  const dateFormatter = (timestamp: string | undefined): string => {
    const date = new Date(timestamp as string);
    return `${date.toLocaleDateString('de-DE')} ${date.toLocaleTimeString(
      'de-DE'
    )}`;
  };

  if (dataResponse == null || isLoading || isFetching) {
    return <CardSkeleton className="bg-white flex w-full h-96" />;
  }

  if (dataResponse.entries != null && dataResponse.entries.length === 0) {
    return (
      <Card className="bg-white flex w-full h-96 font-bold text-2xl items-center justify-center">
        <h1>Keine Einträge vorhanden oder kein Bereich ausgewählt</h1>
      </Card>
    );
  }

  return (
    <Card className="bg-white flex w-full h-96">
      <ResponsiveContainer width="99%" height="100%">
        <LineChart
          width={500}
          height={300}
          data={dataResponse.entries}
          margin={{
            top: 20,
            right: 50,
            left: 20,
            bottom: 5
          }}
        >
          <CartesianGrid strokeDasharray="5 5" />
          <XAxis
            dataKey="timestamp"
            tickFormatter={dateFormatter}
            minTickGap={100}
            tickSize={15}
            interval="preserveStartEnd"
          />

          {checkpointResponse != null &&
            checkpointResponse.checkpoint != null &&
            checkpointResponse.checkpoint.temperaturePayload
              ?.humidityMinLimit !== 0 && (
              <ReferenceLine
                strokeWidth={2}
                y={
                  checkpointResponse.checkpoint.temperaturePayload
                    ?.humidityMinLimit
                }
                stroke="red"
              />
            )}
          {checkpointResponse != null &&
            checkpointResponse.checkpoint != null &&
            checkpointResponse.checkpoint.temperaturePayload
              ?.humidityMaxLimit !== 0 && (
              <ReferenceLine
                strokeWidth={2}
                y={
                  checkpointResponse.checkpoint.temperaturePayload
                    ?.humidityMaxLimit
                }
                stroke="red"
              />
            )}

          <YAxis domain={['auto', 'auto']} />
          <Tooltip labelFormatter={dateFormatter} />
          <Legend />
          <Line
            strokeWidth={2}
            name="%"
            type="monotone"
            dataKey="temperaturePayload.humidity"
            stroke="#8884d8"
          />
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
}

export default TemperatureGraph;
